import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import useFlash from "go-alert/AlertMessage";
import handleError from "go-app/services/errors";
import { FormDirty } from "go-form/components/FormDirty";
import { useCustomErrors } from "go-form/hooks";
import { adminApi } from "../../../../../../../../../services/Api/Admin/adminApi";
import { GoLauncherApplicationApi } from "../../../../../../../../../services/Api/Admin/adminTypes";
import GoLauncherApplicationBasicInfoForm from "./GoLauncherApplicationBasicInfoForm";
import GoLauncherApplicationCustomFeaturesForm from "./GoLauncherApplicationCustomFeaturesForm";

interface Props {
	application?: GoLauncherApplicationApi;
}

const GoLauncherApplicationForm = ({ application }: Props) => {
	const form = useForm<GoLauncherApplicationApi>({
		criteriaMode: "all",
		defaultValues: {
			...application,
			custom_features: application?.custom_features
				? [
						...application?.custom_features,
						{
							title: "",
							type: "",
							parameter: "",
						},
				  ]
				: [
						{
							title: "",
							type: "",
							parameter: "",
						},
				  ],
			image_link: application?.icon
				? {
						image: {
							image_data: application.icon.default_link,
						},
				  }
				: undefined,
		},
	});
	const { setError, handleSubmit, formState, reset, clearErrors } = form;
	const { setErrors, validateCustomErrors } = useCustomErrors(setError);
	const { addFlash, addSuccessFlash } = useFlash();
	const [loading, setLoading] = useState(false);
	const history = useHistory();
	const { t } = useTranslation();

	const getParsedDataForm = (data: GoLauncherApplicationApi) => {
		const parsedData = {
			...data,
			custom_features: data.custom_features.filter(
				(customFeature) => customFeature.type || customFeature.title || customFeature.parameter
			),
		};

		if (parsedData.image?.image_data) {
			parsedData.image_link = {
				...parsedData.image_link,
				image: {
					image_data: parsedData.image.image_data,
				},
			};
		}

		return parsedData;
	};

	const onSubmit = handleSubmit(async (data) => {
		if (!validateCustomErrors()) return;

		setLoading(true);
		try {
			const newData = getParsedDataForm(data);
			let res: GoLauncherApplicationApi;
			if (application?.id) {
				res = await adminApi.updateGoLauncherApplication(application.id, newData);
				reset({
					...res,
					custom_features:
						res.custom_features.length > 0
							? [
									...res.custom_features,
									{
										title: "",
										type: "",
										parameter: "",
									},
							  ]
							: [
									{
										title: "",
										type: "",
										parameter: "",
									},
							  ],
				});
			} else {
				res = await adminApi.createGoLauncherApplication(newData);
				history.push(`/admin/go_launcher/applications/${res.id}`);
			}
			addSuccessFlash(t("lib:common.flash.saved"));
		} catch (err) {
			handleError.form(err, setError, addFlash);
		}
		setLoading(false);
	});

	return (
		<FormDirty
			onSubmit={(e) => {
				clearErrors();
				onSubmit(e);
			}}
			formState={formState}
			loading={loading}
		>
			<GoLauncherApplicationBasicInfoForm form={form} setErrors={setErrors} application={application} />
			<GoLauncherApplicationCustomFeaturesForm form={form} />
		</FormDirty>
	);
};

export default GoLauncherApplicationForm;
